import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { matchPathname } from '../util/routes';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Map library is shown on some of the pages, but ReusableMapContainer is used app wide.
 * However, we can defer the map library loading on pages that don't show the map immediately.
 * Note: this currently only affects Mapbox library.
 * Google Maps library is always loaded immediately. (It seems to be more fragile when loaded asynchronously.)
 *
 * @param {string} initialPathname - The initial pathname at the time of the full page load.
 * @param {array} routeConfiguration - The route configuration.
 * @returns {boolean} - True if the map library can be deferred, false otherwise.
 */
const canDeferMapLibrary = (initialPathname, routeConfiguration) => {
  if (!initialPathname) {
    return false;
  }
  const matchedRoutes = matchPathname(initialPathname, routeConfiguration);
  const currentRouteConfig = matchedRoutes.length > 0 ? matchedRoutes[0]?.route : null;
  return currentRouteConfig?.prioritizeMapLibraryLoading !== true;
};

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains } = analytics;

  const routeConfiguration = useRouteConfiguration();
  // Note: Affects Mapbox only. Google Maps initialization is not yet ready to support asynchronous loading.
  const deferMapLibrary = canDeferMapLibrary(props?.initialPathname, routeConfiguration)
    ? { defer: '' }
    : {};

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    mapLibraries.push(
      <script
        key="mapboxSDK"
        src={`${rootURL}/static/scripts/mapbox/mapbox-sdk@0.16.2/mapbox-sdk.min.js`}
        async
      ></script>
    );
    // License information for v3.7.0 of the mapbox-gl-js library:
    // https://github.com/mapbox/mapbox-gl-js/blob/v3.7.0/LICENSE.txt

    // Add CSS for Mapbox map
    mapLibraries.push(
      <link
        key="mapbox_GL_CSS"
        href="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css"
        rel="stylesheet"
        crossOrigin="anonymous"
      />
    );
    // Add Mapbox library.
    // NOTE — no `crossOrigin="anonymous"`. We don't use Subresource
    // Integrity on this tag, so the only thing crossOrigin gets us
    // is nicer error stack traces. In return it makes the browser
    // strict about the response's CORS headers — and on the Render
    // preview environment (rent-the-event.onrender.com) the browser
    // silently refuses to execute the script if anything in the
    // response chain is missing the right Access-Control-Allow-Origin
    // header. The script tag ends up present in the DOM but
    // `window.mapboxgl` never gets set, which is the recurring
    // "Address autocomplete is loading…" failure. Without
    // crossOrigin the browser treats it as a normal cross-origin
    // script: download, execute, no CORS check.
    mapLibraries.push(
      <script
        id={MAPBOX_SCRIPT_ID}
        key="mapbox_GL_JS"
        src="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js"
        {...deferMapLibrary}
      ></script>
    );
  } else if (isGoogleMapsInUse) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin="anonymous"
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin="anonymous"
      ></script>
    );

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      // Ensure that gtag function is found from window scope
      window.gtag = function gtag() {
        dataLayer.push(arguments);
      };
      gtag('js', new Date());
      gtag('config', googleAnalyticsId, {
        cookie_flags: 'SameSite=None;Secure',
        // Suppress gtag's automatic initial page_view. We send ALL
        // page_views ourselves from src/analytics/handlers.js — including
        // the first one — with a slug-stripped canonical path + title, so
        // a listing is never counted under both /l/{id} and
        // /l/{slug}/{id}. (This is a SPA; the default auto page_view would
        // report the full initial URL, disagreeing with our in-app-nav
        // page_views.)
        send_page_view: false,
      });
    }
  }

  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin="anonymous"
      ></script>
    );
  }

  // Set window.mapboxgl.accessToken once the Mapbox script has
  // loaded. This effect is the single source of truth for token
  // attachment — replacing two earlier mechanisms that both failed
  // for production SSR:
  //   - A render-time `if (window.mapboxgl)` set, which only fired
  //     when the script happened to be loaded by render time. With
  //     defer'd scripts that's almost never on first render.
  //   - A Helmet `onChangeClientState` listener that only attached
  //     to scripts Helmet ADDS on the client. SSR-rendered <script>
  //     tags are already in the DOM at hydration time, so they
  //     never appeared in addedTags.scriptTags and the listener
  //     never got attached.
  // Net effect of the prior code: the access token only got set if
  // IncludeScripts happened to re-render *after* the script
  // finished loading. Pure luck. Explains why address autocomplete
  // failed intermittently on first interaction.
  // The useEffect below finds the SSR-rendered script by id, checks
  // if it's already loaded (the script may have finished before
  // hydration completed — in that case 'load' has already fired
  // and won't fire again), and otherwise attaches a load listener.
  // Either path sets the token; SSR and client-rendered tags are
  // both covered.
  useEffect(() => {
    if (typeof window === 'undefined' || !isMapboxInUse || !mapboxAccessToken) {
      return undefined;
    }

    const setToken = () => {
      if (window.mapboxgl && !window.mapboxgl.accessToken) {
        window.mapboxgl.accessToken = mapboxAccessToken;
      }
    };

    // Script may have finished loading before this effect ran.
    if (window.mapboxgl) {
      setToken();
      return undefined;
    }

    const scriptEl = document.getElementById(MAPBOX_SCRIPT_ID);
    if (!scriptEl) return undefined;

    scriptEl.addEventListener('load', setToken, { once: true });
    return () => scriptEl.removeEventListener('load', setToken);
  }, [isMapboxInUse, mapboxAccessToken]);

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet>{allScripts}</Helmet>;
};
